export default function LandValueSection() {
  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-900" style={{ fontFamily: 'Poppins, sans-serif' }}>
            What Is My Land Worth?
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Understanding your land's value is the first step to selling. Here are the key factors that determine what buyers will pay for vacant land in El Paso and surrounding areas.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div className="w-full h-96 rounded-lg overflow-hidden shadow-2xl">
            <img
              src="https://public.readdy.ai/ai/img_res/eb567b0e6a427d842ce289e9fa402acd.jpg"
              alt="Land valuation factors"
              className="w-full h-full object-cover"
            />
          </div>

          <div>
            <h3 className="text-3xl font-bold mb-6 text-gray-900">Primary Value Drivers</h3>
            <div className="space-y-4">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Location, Location, Location</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Land in established neighborhoods or near growing areas commands premium prices. Proximity to El Paso city limits, major highways, schools, and shopping significantly impacts value. Rural land far from amenities sells for much less per acre.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-ruler-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Size & Shape</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Smaller residential lots (under 1 acre) typically sell for higher per-acre prices than large rural parcels. Irregular shapes, landlocked parcels, or properties with poor access sell at discounts. Buildable lots with good dimensions command premiums.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-teal-100 rounded-lg flex-shrink-0">
                  <i className="ri-road-map-line text-teal-600 text-2xl"></i>
                </div>
                <div>
                  <h4 className="font-bold text-gray-900 mb-2">Access & Road Frontage</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Paved road frontage is gold. Land accessible only by dirt roads or requiring easements through other properties sells for 30-50% less. Corner lots with access from two streets are more valuable than interior lots.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-50 rounded-lg p-8 mb-12">
          <h3 className="text-3xl font-bold mb-8 text-gray-900">Agricultural vs. Residential Land Value</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h4 className="text-2xl font-bold mb-4 text-teal-600">Residential Land</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Land zoned for residential development (R1, R2, R3) typically sells for $10,000-$50,000 per acre in El Paso, depending on location and utilities. Infill lots in established neighborhoods can exceed $100,000 per acre.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Key factors:</strong> Zoning allows single-family homes, utilities at the street, paved road access, and proximity to schools and shopping.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Example:</strong> A 0.5-acre buildable lot in East El Paso with water, sewer, and electric might sell for $25,000-$40,000.
              </p>
            </div>

            <div className="bg-white rounded-lg p-6 shadow-lg">
              <h4 className="text-2xl font-bold mb-4 text-gray-900">Agricultural Land</h4>
              <p className="text-gray-700 leading-relaxed mb-4">
                Agricultural or rural land sells for $1,000-$5,000 per acre in outlying areas. Large ranches (100+ acres) may sell for even less per acre due to limited buyer pool and lack of development potential.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                <strong>Key factors:</strong> Agricultural zoning, no utilities, dirt road access, distance from city services, and limited development rights.
              </p>
              <p className="text-gray-700 leading-relaxed">
                <strong>Example:</strong> A 20-acre agricultural parcel in Chaparral, NM with no utilities might sell for $30,000-$60,000 ($1,500-$3,000 per acre).
              </p>
            </div>
          </div>
        </div>

        <div className="bg-teal-50 border-l-4 border-teal-600 p-8 rounded-lg">
          <h3 className="text-2xl font-bold mb-4 text-gray-900">How We Determine Your Land's Value</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            At TRex RE LLC, we use a comprehensive approach to valuation:
          </p>
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
              <span><strong>Comparable Sales Analysis:</strong> We research recent sales of similar properties within 1-2 miles of your land</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
              <span><strong>County Records Review:</strong> We examine tax assessments, zoning, and any recorded restrictions or easements</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
              <span><strong>Physical Inspection:</strong> When needed, we visit the property to assess topography, access, and condition</span>
            </li>
            <li className="flex items-start">
              <i className="ri-check-line text-teal-600 text-xl mr-3 mt-1"></i>
              <span><strong>Market Conditions:</strong> We factor in current demand, interest rates, and local economic trends</span>
            </li>
          </ul>
          <p className="text-gray-700 leading-relaxed mt-6">
            Our goal is to make you a fair offer that reflects true market value while accounting for the speed and convenience of a cash sale. We're transparent about our valuation process and happy to explain how we arrived at our offer.
          </p>
        </div>
      </div>
    </section>
  );
}
